const ROLE_LIKE_NAMES = new Set([
  'staff',
  'admin',
  'super_admin',
  'super admin',
  'technician',
  'technical_coordinator',
  'admin_officer',
  'accountant',
  'super_accountant',
  'cashier',
  'manager',
  'pos user',
  'unknown',
  'user',
  ''
]);

export const getUserRole = () => {
  const raw = (localStorage.getItem('erp_user_role') || 'staff')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  if (raw === 'technical_cordinator' || raw === 'tech_coordinator') return 'technical_coordinator';
  return raw;
};

export const normalizeRole = (role) => {
  const r = String(role || 'staff')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  if (r === 'technical_cordinator' || r === 'tech_coordinator') return 'technical_coordinator';
  return r;
};

/** True if value looks like a role label, not a person name */
export const isInvalidPersonName = (name) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (ROLE_LIKE_NAMES.has(lower)) return true;
  if (ROLE_LIKE_NAMES.has(normalizeRole(trimmed))) return true;
  return false;
};

/**
 * Logged-in person's display name (e.g. "Chamila"), never the role ("staff").
 * Prefers full_name from erp_user JSON, then erp_username, username, email.
 */
export const getCurrentUserName = () => {
  try {
    const raw = localStorage.getItem('erp_user');
    if (raw) {
      const user = JSON.parse(raw);
      const candidates = [
        user.full_name,
        user.name,
        user.username,
        user.email
      ];
      for (const c of candidates) {
        if (!isInvalidPersonName(c)) return String(c).trim();
      }
    }
  } catch (_) {
    /* ignore */
  }

  const stored = localStorage.getItem('erp_username');
  if (!isInvalidPersonName(stored)) return stored.trim();

  const email = localStorage.getItem('erp_user_email');
  if (email && email.includes('@')) {
    const local = email.split('@')[0];
    if (local) return local;
  }

  return 'User';
};

export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem('erp_user');
    if (raw) return JSON.parse(raw);
  } catch (_) {
    /* ignore */
  }
  return {
    id: localStorage.getItem('erp_user_id'),
    full_name: getCurrentUserName(),
    email: localStorage.getItem('erp_user_email'),
    role: getUserRole(),
    branch_id: localStorage.getItem('erp_branch_id'),
    branch_name: localStorage.getItem('erp_branch_name'),
    branch_code: localStorage.getItem('erp_branch_code')
  };
};

/** Branch label for header: "Palawaththata (PWT)" */
export const getCurrentBranchLabel = () => {
  const name = localStorage.getItem('erp_branch_name') || '';
  const code = localStorage.getItem('erp_branch_code') || '';
  try {
    const raw = localStorage.getItem('erp_user');
    if (raw) {
      const u = JSON.parse(raw);
      const n = u.branch_name || name;
      const c = u.branch_code || code;
      if (n && c) return `${n} (${c})`;
      if (n) return n;
      if (c) return c;
    }
  } catch (_) {
    /* ignore */
  }
  if (name && code) return `${name} (${code})`;
  return name || code || '';
};

/** Super Admin or Admin */
export const isFullAdmin = () => {
  const r = getUserRole();
  return r === 'super_admin' || r === 'admin';
};

/** Super admin, admin, super accountant — all-branch filter + shift */
export const canAccessAllBranches = () => {
  const r = getUserRole();
  return r === 'super_admin' || r === 'admin' || r === 'super_accountant';
};

export const canShiftBranches = () => canAccessAllBranches();

export const isSuperAdmin = () => getUserRole() === 'super_admin';

export const isAdmin = () => isFullAdmin();

export const canViewFinancials = () => isFullAdmin() || getUserRole() === 'super_accountant';

/** Destructive deletes — Super Admin only */
export const canDelete = () => isSuperAdmin();

/** Product edit/delete — Admin + Super Admin only */
export const canEditProducts = () => isFullAdmin();
export const canDeleteProducts = () => isFullAdmin();

export const canApproveGrn = () => isFullAdmin();

export const isTechnicalCoordinator = () => getUserRole() === 'technical_coordinator';
export const isAdminOfficer = () => getUserRole() === 'admin_officer';
export const isTechnician = () => getUserRole() === 'technician';

/** Repair quotation / diagnosis admin actions */
export const canManageRepairs = () => {
  const r = getUserRole();
  return [
    'super_admin',
    'admin',
    'manager',
    'branch_manager',
    'technical_coordinator',
    'admin_officer'
  ].includes(r);
};

/** Outsource repair price / cost visibility */
export const canViewOutsourcePrice = () => {
  const r = getUserRole();
  return [
    'super_admin',
    'admin',
    'super_accountant',
    'accountant',
    'staff',
    'technical_coordinator',
    'admin_officer',
    'manager'
  ].includes(r);
};
